<template>
    <div class="artigos-management">
        <filter-bar class="mb-6">
            <v-text-field
                v-model="filters.search"
                prepend-inner-icon="mdi-magnify"
                placeholder="Buscar por título ou assunto"
                outlined
                dense
                hide-details
                clearable
                @input="onSearchInput"
                @click:clear="onSearchInput"
            />
            <v-select
                v-model="filters.status"
                :items="statusOptions"
                item-text="text"
                item-value="value"
                placeholder="Todos os status"
                outlined
                dense
                hide-details
                clearable
            />
            <v-switch
                v-if="isAdmin"
                v-model="filters.onlyMine"
                inset
                dense
                hide-details
                color="primary"
                class="mt-0 pt-0 mine-switch"
                :label="filters.onlyMine ? 'Só os meus' : 'Todos os autores'"
            />
            <template #trailing>
                <v-btn
                    icon
                    :loading="loading"
                    title="Atualizar"
                    @click="fetch"
                >
                    <v-icon>mdi-refresh</v-icon>
                </v-btn>
            </template>
        </filter-bar>

        <glass-data-table
            class="management-table"
            :headers="computedHeaders"
            :items="articles"
            :loading="loading"
            loading-text="Carregando artigos..."
            no-data-text="Nenhum artigo encontrado."
            :items-per-page="15"
            :footer-props="{ itemsPerPageOptions: [15, 30, 50, -1], itemsPerPageText: 'Por página' }"
        >
                <template #[`item.title`]="{ item }">
                    <div class="d-flex align-center">
                        <div class="title-thumb" :class="{ 'title-thumb--empty': !item.publish_main_image }">
                            <img v-if="item.publish_main_image" :src="item.publish_main_image" :alt="item.title" />
                            <v-icon v-else x-small color="mercury">mdi-image-outline</v-icon>
                        </div>
                        <div class="title-text">
                            <div class="title-primary one-line" :title="item.title || item.subject || ''">
                                {{ item.title || item.subject || '(sem título)' }}
                            </div>
                            <div v-if="item.subject && item.title && item.subject !== item.title" class="title-sub one-line" :title="item.subject">
                                {{ item.subject }}
                            </div>
                        </div>
                    </div>
                </template>

                <template #[`item.user.name`]="{ item }">
                    <span class="author-name one-line" :title="item.user?.email || ''">{{ item.user?.name || '—' }}</span>
                </template>

                <template #[`item.publish_status`]="{ item }">
                    <RChipStatus
                        :value="normalizedStatus(item)"
                        :items="statusChipItems"
                        x-small
                    />
                </template>

                <template #[`item.seo_score`]="{ item }">
                    <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                            <span
                                v-if="hasSeoScore(item)"
                                class="seo-badge"
                                :class="`seo-badge--${seoBadgeColor(item.seo_score)}`"
                                v-bind="attrs"
                                v-on="on"
                            >{{ item.seo_score }}</span>
                            <span
                                v-else
                                class="seo-badge seo-badge--empty"
                                v-bind="attrs"
                                v-on="on"
                            >—</span>
                        </template>
                        <span>{{ seoBadgeTooltip(item) }}</span>
                    </v-tooltip>
                </template>

                <template #[`item.dates`]="{ item }">
                    <div class="dates-cell">
                        <div class="dates-row">
                            <span class="dates-label">Criado</span>
                            <span class="dates-value">
                                {{ formatShortDate(item.created) }}
                                <small v-if="formatShortTime(item.created)">{{ formatShortTime(item.created) }}</small>
                            </span>
                        </div>
                        <div class="dates-row">
                            <span
                                class="dates-label"
                                :class="`dates-label--${normalizedStatus(item)}`"
                            >
                                {{ publicationLabel(item) }}
                            </span>
                            <span class="dates-value">
                                {{ item.publish_date ? formatShortDate(item.publish_date) : '—' }}
                                <small v-if="item.publish_date && formatShortTime(item.publish_date)">{{ formatShortTime(item.publish_date) }}</small>
                            </span>
                        </div>
                    </div>
                </template>

                <template #[`item.actions`]="{ item }">
                    <div class="d-flex justify-end">
                        <v-menu
                            offset-y
                            left
                            close-on-content-click
                            min-width="190"
                            content-class="article-actions-menu"
                        >
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    icon
                                    small
                                    color="mercury"
                                    title="Ações"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>

                            <v-list
                                dense
                                class="article-actions-list"
                            >
                                <v-list-item @click="$emit('view', item)">
                                    <v-list-item-icon>
                                        <v-icon small>mdi-eye-outline</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title>Visualizar</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>

                                <v-list-item
                                    v-if="canEdit(item)"
                                    @click="$emit('edit', item)"
                                >
                                    <v-list-item-icon>
                                        <v-icon
                                            small
                                            color="primary"
                                        >
                                            mdi-file-edit-outline
                                        </v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title>Editar conteúdo</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>

                                <v-list-item
                                    v-if="canPublish(item)"
                                    @click="$emit('publish', item)"
                                >
                                    <v-list-item-icon>
                                        <v-icon
                                            small
                                            color="primary"
                                        >
                                            mdi-send-outline
                                        </v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ normalizedStatus(item) === 'scheduled' ? 'Reagendar / publicar' : 'Publicar ou agendar' }}</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>

                                <v-list-item
                                    v-if="canOpenOnSite(item)"
                                    :href="item.duda_link"
                                    target="_blank"
                                    rel="noopener"
                                >
                                    <v-list-item-icon>
                                        <v-icon
                                            small
                                            color="success"
                                        >
                                            mdi-open-in-new
                                        </v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title>Abrir no site</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>

                                <v-list-item
                                    v-if="canDelete(item)"
                                    @click="$emit('delete', item)"
                                >
                                    <v-list-item-icon>
                                        <v-icon
                                            small
                                            color="error"
                                        >
                                            mdi-trash-can-outline
                                        </v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title>Excluir</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>
                </template>
        </glass-data-table>

        <div class="mobile-article-list">
            <glass-panel
                v-if="loading"
                radius="lg"
                class="mobile-article-state pa-5"
            >
                <v-progress-linear
                    indeterminate
                    color="primary"
                    height="4"
                    rounded
                    class="mb-3"
                />
                <div class="body-sm ma-0">Carregando artigos...</div>
            </glass-panel>

            <glass-panel
                v-else-if="!articles.length"
                radius="lg"
                class="mobile-article-state pa-5"
            >
                <v-icon
                    color="mercury"
                    size="24"
                    class="mb-2"
                >
                    mdi-file-document-outline
                </v-icon>
                <div class="card-title-strong mobile-article-state__title">Nenhum artigo encontrado.</div>
            </glass-panel>

            <template v-else>
                <article
                    v-for="item in articles"
                    :key="item._id"
                    class="mobile-article-card"
                >
                    <div class="mobile-article-card__top">
                        <div
                            class="mobile-article-thumb"
                            :class="{ 'title-thumb--empty': !item.publish_main_image }"
                        >
                            <img
                                v-if="item.publish_main_image"
                                :src="item.publish_main_image"
                                :alt="item.title"
                            />
                            <v-icon
                                v-else
                                small
                                color="mercury"
                            >
                                mdi-image-outline
                            </v-icon>
                        </div>

                        <div class="mobile-article-card__body">
                            <div class="mobile-article-card__badges">
                                <RChipStatus
                                    :value="normalizedStatus(item)"
                                    :items="statusChipItems"
                                    x-small
                                />
                                <span
                                    v-if="hasSeoScore(item)"
                                    class="seo-badge"
                                    :class="`seo-badge--${seoBadgeColor(item.seo_score)}`"
                                    :title="seoBadgeTooltip(item)"
                                >
                                    SEO {{ item.seo_score }}
                                </span>
                                <span
                                    v-else
                                    class="seo-badge seo-badge--empty"
                                    :title="seoBadgeTooltip(item)"
                                >
                                    SEO —
                                </span>
                            </div>

                            <h3
                                class="mobile-article-title"
                                :title="item.title || item.subject || ''"
                            >
                                {{ item.title || item.subject || '(sem título)' }}
                            </h3>
                            <p
                                v-if="item.subject && item.title && item.subject !== item.title"
                                class="mobile-article-subject"
                            >
                                {{ item.subject }}
                            </p>
                        </div>
                    </div>

                    <div class="mobile-article-meta">
                        <div v-if="showAuthorColumn" class="mobile-article-meta__item">
                            <span>Autor</span>
                            <strong>{{ item.user?.name || '—' }}</strong>
                        </div>
                        <div class="mobile-article-meta__item">
                            <span>Criação</span>
                            <strong>
                                {{ formatShortDate(item.created) }}
                                <small v-if="formatShortTime(item.created)">{{ formatShortTime(item.created) }}</small>
                            </strong>
                        </div>
                        <div class="mobile-article-meta__item">
                            <span>Publicação</span>
                            <strong>
                                {{ item.publish_date ? formatShortDate(item.publish_date) : '—' }}
                                <small v-if="item.publish_date && formatShortTime(item.publish_date)">{{ formatShortTime(item.publish_date) }}</small>
                            </strong>
                        </div>
                    </div>

                    <div class="mobile-article-actions">
                        <v-btn
                            small
                            depressed
                            color="primary"
                            @click="$emit('view', item)"
                        >
                            <v-icon
                                left
                                small
                            >
                                mdi-eye-outline
                            </v-icon>
                            Visualizar
                        </v-btn>

                        <v-btn
                            v-if="canEdit(item)"
                            icon
                            small
                            color="primary"
                            title="Editar conteúdo"
                            @click="$emit('edit', item)"
                        >
                            <v-icon small>mdi-file-edit-outline</v-icon>
                        </v-btn>

                        <v-btn
                            v-if="canPublish(item)"
                            icon
                            small
                            color="primary"
                            :title="normalizedStatus(item) === 'scheduled' ? 'Reagendar / publicar' : 'Publicar ou agendar'"
                            @click="$emit('publish', item)"
                        >
                            <v-icon small>mdi-send-outline</v-icon>
                        </v-btn>

                        <v-btn
                            v-if="canOpenOnSite(item)"
                            icon
                            small
                            color="success"
                            title="Abrir no site"
                            :href="item.duda_link"
                            target="_blank"
                            rel="noopener"
                        >
                            <v-icon small>mdi-open-in-new</v-icon>
                        </v-btn>

                        <v-btn
                            v-if="canDelete(item)"
                            icon
                            small
                            color="error"
                            title="Excluir"
                            @click="$emit('delete', item)"
                        >
                            <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                    </div>
                </article>
            </template>
        </div>
    </div>
</template>

<script>
import Api from '@/lib/Api'
import { emitToastr } from '@/lib/Utils'
import RChipStatus from '@/components/RChipStatus.vue'
import { seoScoreColor, seoScoreLabel } from '@/lib/seoScore'

export default {
    name: 'ArtigosManagementList',
    components: { RChipStatus },
    data() {
        return {
            articles: [],
            loading: false,
            searchDebounce: null,
            filters: {
                search: '',
                status: null,
                onlyMine: false,
            },
            statusOptions: [
                { text: 'Todos os status', value: null },
                { text: 'Rascunho', value: 'draft' },
                { text: 'Agendado', value: 'scheduled' },
                { text: 'Publicado', value: 'published' },
            ],
            statusChipItems: [
                { value: 'draft', text: 'Rascunho', color: 'grey lighten-2', dark: false },
                { value: 'scheduled', text: 'Agendado', color: 'amber darken-1', dark: true },
                { value: 'published', text: 'Publicado', color: 'success', dark: true },
            ],
        }
    },
    computed: {
        currentUser() {
            return this.$store?.state?.currentUser || null
        },
        isAdmin() {
            return !!(this.currentUser?.admin || this.currentUser?.role === 'admin')
        },
        isSimpleUser() {
            return this.currentUser?.role === 'user'
        },
        showAuthorColumn() {
            return !this.isSimpleUser
        },
        computedHeaders() {
            const base = [
                { text: 'Título', value: 'title', sortable: false },
            ]
            if (this.showAuthorColumn) {
                base.push({ text: 'Autor', value: 'user.name', sortable: false, width: 128 })
            }
            base.push(
                { text: 'Status', value: 'publish_status', sortable: false, width: 110 },
                { text: 'SEO', value: 'seo_score', sortable: true, width: 76, align: 'center' },
                { text: 'Datas', value: 'dates', sortable: false, width: 150 },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
            )
            return base
        },
    },
    watch: {
        'filters.status'() { this.fetch() },
        'filters.onlyMine'() { this.fetch() },
    },
    mounted() {
        this.fetch()
    },
    methods: {
        onSearchInput() {
            if (this.searchDebounce) clearTimeout(this.searchDebounce)
            this.searchDebounce = setTimeout(() => this.fetch(), 300)
        },
        async fetch() {
            this.loading = true
            try {
                const payload = {}
                if (this.filters.search && this.filters.search.trim()) payload.search = this.filters.search.trim()
                if (this.filters.status) payload.status = this.filters.status
                if (this.isAdmin && this.filters.onlyMine) payload.onlyMine = true

                const resp = await Api.listArticlesV2(payload)
                if (resp?.error) {
                    emitToastr('error', resp.message || 'Erro ao carregar artigos.')
                    return
                }
                this.articles = Array.isArray(resp?.message) ? resp.message : []
            } catch (err) {
                emitToastr('error', 'Erro ao carregar artigos', err?.message || '')
            } finally {
                this.loading = false
            }
        },
        refresh() { this.fetch() },
        removeById(article_id) {
            const i = this.articles.findIndex(a => a._id === article_id)
            if (i !== -1) this.articles.splice(i, 1)
        },
        upsertArticle(article) {
            if (!article || !article._id) return
            const i = this.articles.findIndex(a => a._id === article._id)
            const merged = i === -1 ? article : { ...this.articles[i], ...article }
            if (i === -1) this.articles.unshift(merged)
            else this.$set(this.articles, i, merged)
        },
        normalizedStatus(article) {
            const s = article?.publish_status
            if (s === 'published') return 'published'
            if (s === 'scheduled') return 'scheduled'
            return 'draft'
        },
        publicationLabel(article) {
            const status = this.normalizedStatus(article)
            if (status === 'published') return 'Publicado'
            if (status === 'scheduled') return 'Agendado'
            return 'Não publ.'
        },
        canEdit(article) {
            return this.normalizedStatus(article) !== 'published'
        },
        canPublish(article) {
            return this.normalizedStatus(article) !== 'published'
        },
        canDelete(article) {
            return this.normalizedStatus(article) !== 'published'
        },
        canOpenOnSite(article) {
            return this.normalizedStatus(article) === 'published' && !!article?.duda_link
        },
        hasSeoScore(article) {
            return typeof article?.seo_score === 'number' && article.seo_score > 0
        },
        seoBadgeColor(score) {
            const c = seoScoreColor(score)
            if (c === 'success') return 'good'
            if (c === 'warning') return 'ok'
            if (c === 'error') return 'bad'
            return 'na'
        },
        seoBadgeTooltip(article) {
            if (!this.hasSeoScore(article)) return 'Score SEO não calculado — abra e salve o artigo para gerar.'
            return `SEO Score: ${article.seo_score}/100 · ${seoScoreLabel(article.seo_score)}`
        },
        formatDate(value) {
            if (!value) return '—'
            try {
                return new Date(value).toLocaleString('pt-BR', {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                })
            } catch {
                return '—'
            }
        },
        formatShortDate(value) {
            if (!value) return '—'
            try {
                return new Date(value).toLocaleDateString('pt-BR', {
                    day: '2-digit', month: '2-digit', year: '2-digit',
                })
            } catch {
                return '—'
            }
        },
        formatShortTime(value) {
            if (!value) return ''
            try {
                return new Date(value).toLocaleTimeString('pt-BR', {
                    hour: '2-digit', minute: '2-digit',
                })
            } catch {
                return ''
            }
        },
    },
}
</script>

<style scoped>
.artigos-management {
    width: 100%;
}

.mobile-article-list {
    display: none;
}

.title-thumb {
    width: 36px;
    height: 36px;
    border-radius: var(--radius-xs);
    background: var(--glass-soft-bg);
    flex-shrink: 0;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    border: 1px solid var(--glass-panel-border);
}

.title-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.title-text {
    min-width: 0;
    flex: 1;
}

.title-primary {
    font-weight: 600;
    font-size: 13.5px;
    color: var(--color-on-surface);
    line-height: 1.3;
}

.title-sub {
    font-size: 11.5px;
    color: var(--color-on-surface-variant);
    margin-top: 1px;
}

.one-line {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.author-name {
    display: block;
    font-size: 13px;
    color: var(--color-on-surface);
    font-weight: 500;
    width: 112px;
    max-width: 112px;
}

.dates-cell,
.date-cell {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    line-height: 1.2;
    min-width: 130px;
    gap: 4px;
}

.dates-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
}

.dates-label {
    flex: 0 0 auto;
    color: var(--color-on-surface-variant);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.07em;
    text-transform: uppercase;
}

.dates-label--published {
    color: var(--color-success);
}

.dates-label--scheduled {
    color: #b45309;
}

html.theme-dark .dates-label--scheduled {
    color: #fbbf24;
}

.dates-label--draft {
    color: var(--color-on-surface-variant);
}

.dates-value {
    min-width: 0;
    color: var(--color-on-surface);
    font-size: 12px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    text-align: right;
    white-space: nowrap;
}

.dates-value small {
    color: var(--color-on-surface-variant);
    font-size: 11px;
    font-weight: 600;
    margin-left: 3px;
}

.date-main {
    font-size: 12.5px;
    color: var(--color-on-surface);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.date-time {
    font-size: 11px;
    color: var(--color-on-surface-variant);
    font-variant-numeric: tabular-nums;
}

.mine-switch >>> .v-label {
    font-size: 13px;
    color: var(--color-on-surface-variant);
}

.article-actions-list {
    padding: 4px;
}

.article-actions-list >>> .v-list-item {
    min-height: 34px;
    border-radius: 8px;
}

.article-actions-list >>> .v-list-item__icon {
    margin: 8px 10px 8px 0;
}

.article-actions-list >>> .v-list-item__title {
    font-size: 13px;
    font-weight: 600;
}

.seo-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    height: 24px;
    padding: 0 8px;
    border-radius: var(--radius-pill);
    font-size: 12px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    line-height: 1;
    border: 1px solid transparent;
    cursor: default;
}

.seo-badge--good {
    background: var(--color-success-soft);
    color: var(--color-success);
    border-color: var(--color-success-soft);
}

.seo-badge--ok {
    background: rgba(245, 158, 11, 0.14);
    color: #b45309;
    border-color: rgba(245, 158, 11, 0.30);
}

html.theme-dark .seo-badge--ok {
    color: #fbbf24;
}

.seo-badge--bad {
    background: rgba(239, 68, 68, 0.12);
    color: var(--color-error);
    border-color: rgba(239, 68, 68, 0.28);
}

.seo-badge--empty {
    background: transparent;
    color: var(--color-on-surface-variant);
    font-weight: 500;
}

@media (max-width: 700px) {
    .management-table {
        display: none;
    }

    .mobile-article-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .artigos-management >>> .filter-bar {
        align-items: stretch;
    }

    .artigos-management >>> .filter-bar__filters {
        flex-direction: column;
        align-items: stretch;
    }

    .artigos-management >>> .filter-bar__filters > * {
        width: 100%;
        max-width: none;
        flex: 1 1 auto;
    }

    .artigos-management >>> .filter-bar__trailing {
        justify-content: flex-end;
    }

    .mobile-article-state {
        text-align: center;
    }

    .mobile-article-state__title {
        font-size: 0.95rem;
    }

    .mobile-article-card {
        padding: 14px;
        border-radius: var(--radius-md);
        border: 1px solid var(--glass-panel-border);
        background: var(--glass-panel-bg);
        -webkit-backdrop-filter: blur(16px);
        backdrop-filter: blur(16px);
        box-shadow: var(--shadow-glass);
    }

    .mobile-article-card__top {
        display: grid;
        grid-template-columns: 64px minmax(0, 1fr);
        gap: 12px;
        align-items: start;
    }

    .mobile-article-thumb {
        width: 64px;
        height: 64px;
        border-radius: 10px;
        background: var(--glass-soft-bg);
        border: 1px solid var(--glass-panel-border);
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .mobile-article-thumb img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    .mobile-article-card__body {
        min-width: 0;
    }

    .mobile-article-card__badges {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
        margin-bottom: 8px;
    }

    .mobile-article-card__badges .seo-badge {
        min-width: 0;
        height: 22px;
        padding: 0 8px;
        font-size: 11px;
    }

    .mobile-article-title {
        margin: 0;
        color: var(--color-on-surface);
        font-size: 0.95rem;
        font-weight: 700;
        line-height: 1.35;
        overflow-wrap: anywhere;
    }

    .mobile-article-subject {
        margin: 4px 0 0;
        color: var(--color-on-surface-variant);
        font-size: 0.8125rem;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .mobile-article-meta {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid var(--glass-panel-border);
    }

    .mobile-article-meta__item {
        min-width: 0;
        padding: 8px;
        border-radius: 10px;
        background: var(--glass-soft-bg);
    }

    .mobile-article-meta__item:first-child:nth-last-child(3) {
        grid-column: 1 / -1;
    }

    .mobile-article-meta__item span {
        display: block;
        color: var(--color-on-surface-variant);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.08em;
        line-height: 1.2;
        text-transform: uppercase;
    }

    .mobile-article-meta__item strong {
        display: block;
        min-width: 0;
        margin-top: 4px;
        color: var(--color-on-surface);
        font-size: 0.8125rem;
        font-weight: 700;
        line-height: 1.25;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .mobile-article-meta__item small {
        color: var(--color-on-surface-variant);
        font-size: 0.75rem;
        font-weight: 600;
        margin-left: 4px;
    }

    .mobile-article-actions {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 12px;
    }

    .mobile-article-actions .v-btn:not(.v-btn--icon) {
        flex: 1 1 auto;
        min-width: 132px;
    }
}
</style>
